/**
 * pages/admin.ts —— 管理后台的【壳】：页头 + Tab 条 + 按 tab 分发
 * 来历：一期 [SEC:page-admin] 的 adminHtml()，版式不变。
 *
 * ## 三个 Tab 的去处
 *   periods    ✅ W2-2 `pages/admin-periods.ts`
 *   persons    ✅ W2-3 `pages/admin-persons.ts`
 *   analytics  ✅ 2026-09-15 `pages/admin-analytics.ts`（活跃统计，`14_活跃统计方案.MD`）
 *
 * ⚠ 曾有第三个 Tab「系统与数据」（占位，规划中的 Excel 导出）。**2026-08-26 整个拿掉**：
 *   导出功能一并取消（用户决定，留痕见 `02_产品需求基线.md` §1.4 对照表）。
 *   连带删掉的还有 `13_admin.css` 里 `.sysgrid` / `.env` / `.sys-row` / `.plan` 那一段死样式。
 *
 * ⚠ 拆成三个文件是因为两个 Tab 各有一套表格 + 两三个弹窗，挤在一个文件里很快就到 600 行；
 *   **壳只管路由与装配，不认识任何业务字段**——加第三个 Tab 时照着加一行分发即可。
 * ⚠ 本页仅管理员可见：非管理员的 `#/admin` 由 `router/guards.ts` 弹回首页，侧栏也不显示入口。
 *   **那只是界面引导，真正的拦截在服务端**（红线 4）。
 */
import type { AnalyticsOverview, PeriodAdminView, PersonAdminView } from '@shared-types';
import { $ } from '../shared/dom.js';
import * as periodsTab from './admin-periods.js';
import * as personsTab from './admin-persons.js';
import * as analyticsTab from './admin-analytics.js';
import { definePage } from './types.js';

/** 三个 Tab。⚠ 键必须与 router/guards.ts 的 ADM_TABS 一致，那边是路由的唯一出处 */
const TABS: ReadonlyArray<readonly [string, string]> = [
  ['periods', '周期管理'],
  ['persons', '人员管理'],
  ['analytics', '活跃统计'],
];

export interface AdminData {
  tab: string;
  /** 当前 tab 的数据；⚠ 只取当前这一个 tab 的，切过去才拉——两个都拉等于每次多一半开销 */
  periods: PeriodAdminView | null;
  persons: PersonAdminView | null;
  analytics: AnalyticsOverview | null;
}

function toolbarHtml(d: AdminData): string {
  if (d.tab === 'periods' && d.periods) return periodsTab.toolbar(d.periods);
  if (d.tab === 'persons' && d.persons) return personsTab.toolbar(d.persons);
  if (d.tab === 'analytics' && d.analytics) return analyticsTab.toolbar(d.analytics);
  return '';
}

function bodyHtml(d: AdminData): string {
  if (d.tab === 'persons') return d.persons ? personsTab.panel(d.persons) : '';
  if (d.tab === 'analytics') return d.analytics ? analyticsTab.panel(d.analytics) : '';
  // ⚠ 未知 tab 由 router/guards.ts 弹回 periods，这里不会收到第四种值
  return d.periods ? periodsTab.panel(d.periods) : '';
}

export function adminHtml(d: AdminData): string {
  return `
  <div class="page">
    <div class="pagehead">
      <div>
        <h1>管理后台</h1>
        <p class="ph-sub">基础数据管理与使用统计</p>
      </div>
    </div>

    <div class="adm-head">
      <div class="adm-tabs" role="tablist">
        ${TABS.map(([k, lab]) =>
          `<button role="tab" data-admtab="${k}" ${k === d.tab ? 'aria-current="page"' : ''}>${lab}</button>`,
        ).join('')}
      </div>
      ${toolbarHtml(d)}
    </div>

    ${bodyHtml(d)}
  </div>`;
}

/** 上一次渲染的 Tab，只给 mounted 判断要不要回顶 */
let lastTab = '';

export const adminPage = definePage<AdminData>({
  skeleton: 'table',

  async load({ route, signal }) {
    const tab = route.view === 'admin' ? (route.tab ?? 'periods') : 'periods';
    return {
      tab,
      periods: tab === 'periods' ? await periodsTab.load(signal) : null,
      persons: tab === 'persons' ? await personsTab.load(signal) : null,
      analytics: tab === 'analytics' ? await analyticsTab.load(signal) : null,
    };
  },

  html: (d) => adminHtml(d),

  mounted(d) {
    // Tab 切换后回到顶部：从长长的人员表切到周期 Tab 还停在半空会很莫名
    // ⚠ 只在**换了 Tab** 时回顶：活跃统计改粒度 / 时间是同一 Tab 内重新请求，跳回顶部会把人甩离刚操作的图表
    if (d.tab !== lastTab) $('#main')?.scrollTo?.({ top: 0 });
    lastTab = d.tab;
    if (d.tab === 'persons') personsTab.mounted();
  },

  onEvent(e, d) {
    const t = e.target;
    if (!(t instanceof Element)) return;

    /* Tab 切换：走 hash 不直接改 state——hash 里带着 tab，刷新与后退才回得到原来那个 Tab */
    if (e.type === 'click') {
      const tab = t.closest<HTMLElement>('[data-admtab]');
      if (tab) {
        location.hash = '#/admin/' + tab.dataset.admtab;
        return;
      }
      if (d.tab === 'periods' && d.periods) return periodsTab.onClick(t, d.periods);
    }
    // 人员 Tab 还要认 input / change（搜索与两个筛选下拉）
    if (d.tab === 'persons' && d.persons) personsTab.onEvent(e, d.persons);
    // 活跃统计：时间 / 粒度重新请求；展开、搜索、排序只重画对应那一块
    if (d.tab === 'analytics' && d.analytics) analyticsTab.onEvent(e, d.analytics);
  },
});
